/**
 * Tenant list — `GET tenants/tenants/`. The row SHAPE depends on the caller's
 * role server-side: AGENT/SUPERAGENT get `EnhancedTenant` (extra billing
 * columns), LANDLORD gets the plain `User` shape (tenants/routes.ts:496-501).
 * `isEnhancedTenant` narrows per row rather than trusting `useAuth()` alone, so
 * the table never reads a field that isn't actually there.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Column, Grid, InlineLoading, TextInput } from '@carbon/react';
import { Add, Edit, TrashCan } from '@carbon/icons-react';
import { Users } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { Notify } from '../../components/carbon/Notify';
import { Pager } from '../../components/carbon/Pager';
import { PageHeader } from '../../components/carbon/PageHeader';
import {
  ResponsiveDataTable,
  type Column as DataColumn,
} from '../../components/carbon/ResponsiveDataTable';
import { deleteTenant, listTenants } from '../../api/endpoints/tenants';
import { isEnhancedTenant } from '../../types/tenant';
import type { TenantRow } from '../../types/tenant';
import { fullName } from '../../types/user';

/** Both row shapes carry a display name, under different field names. */
function tenantName(tenant: TenantRow): string {
  return isEnhancedTenant(tenant) ? tenant.full_name : fullName(tenant);
}

export function TenantListPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['tenants', { page, search }],
    queryFn: () => listTenants({ page, search: search === '' ? undefined : search }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteTenant(id),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (caught) => setActionError(parseApiError(caught).message),
  });

  const tenants = query.data?.results ?? [];
  const count = query.data?.count ?? 0;
  const pendingTenant = tenants.find((tenant) => tenant.id === confirmDeleteId) ?? null;

  const columns: readonly DataColumn<TenantRow>[] = [
    { key: 'name', header: 'Name', render: (row) => <Link to={String(row.id)}>{tenantName(row)}</Link> },
    { key: 'email', header: 'Email', render: (row) => row.email },
    { key: 'phone', header: 'Phone', render: (row) => (row.phone === '' ? '—' : row.phone) },
    { key: 'property', header: 'Property', render: (row) => row.property_name ?? 'N/A' },
    { key: 'unit', header: 'Unit', render: (row) => row.unit_number ?? 'N/A' },
    { key: 'lease_status', header: 'Lease status', render: (row) => row.lease_status },
    { key: 'balance', header: 'Balance', render: (row) => row.current_balance },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <span className="row-actions">
          <Button kind="ghost" size="sm" as={Link} to={`${row.id}/edit`} renderIcon={Edit} iconDescription="Edit">
            Edit
          </Button>
          <Button
            kind="danger--ghost"
            size="sm"
            disabled={remove.isPending}
            onClick={() => setConfirmDeleteId(row.id)}
            renderIcon={TrashCan}
            iconDescription="Delete"
          >
            Delete
          </Button>
        </span>
      ),
    },
  ];

  return (
    <section>
      <PageHeader
        title="Tenants"
        actions={
          <>
            <Button kind="tertiary" as={Link} to="leases">
              Leases
            </Button>
            <Button as={Link} to="new" renderIcon={Add}>
              Add tenant
            </Button>
          </>
        }
      />

      {actionError !== null && <Notify kind="error" title={actionError} className="page-notify" />}

      <Grid
        className="filter-grid"
        as="form"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
        }}
      >
        <Column sm={4} md={6} lg={10}>
          <TextInput
            id="tenant-search"
            labelText="Search"
            placeholder="Name, email, phone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Column>
        <Column sm={4} md={2} lg={2} className="filter-grid__submit">
          <Button type="submit" kind="secondary">
            Search
          </Button>
        </Column>
      </Grid>

      {query.isPending && <InlineLoading description="Loading tenants…" />}
      {query.isError && <Notify kind="error" title={parseApiError(query.error).message} />}

      {query.isSuccess && tenants.length === 0 && (
        <EmptyState icon={Users} message="No tenants found." action={{ label: 'Add tenant', to: 'new' }} />
      )}

      {query.isSuccess && tenants.length > 0 && (
        <>
          <ResponsiveDataTable caption="Tenants" columns={columns} rows={tenants} rowId={(row) => String(row.id)} />
          <Pager page={page} count={count} onPageChange={setPage} />
        </>
      )}

      <ConfirmDialog
        open={pendingTenant !== null}
        title="Delete tenant"
        message={
          pendingTenant === null
            ? ''
            : `Delete "${tenantName(pendingTenant)}"? This also removes their leases and cannot be undone.`
        }
        tone="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingTenant !== null) remove.mutate(pendingTenant.id);
          setConfirmDeleteId(null);
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </section>
  );
}

export default TenantListPage;
